import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { noop } from 'lodash';

import { BID_TYPES } from '../utilities/biddingState';
import { useFullState } from '../utilities/hooks/hooks';

const VEHICLE_PURCHASE_MODAL_TYPES = Object.freeze({
	BUY_IT_NOW: 'BUY_IT_NOW',
	IDLE: 'IDLE',
	INCREASE_OFFER: 'INCREASE_OFFER',
	MAKE_OFFER: 'MAKE_OFFER',
	PLACE_BID_FROM_FOOTER: 'PLACE_BID_FROM_FOOTER',
	PLACE_BID_FROM_SIDEBAR: 'PLACE_BID_FROM_SIDEBAR',
});

const VEHICLE_PURCHASE_MODAL_BID_TYPES = Object.freeze({
	[VEHICLE_PURCHASE_MODAL_TYPES.PLACE_BID_FROM_SIDEBAR]: BID_TYPES.PROXY,
	[VEHICLE_PURCHASE_MODAL_TYPES.PLACE_BID_FROM_FOOTER]: BID_TYPES.PROXY,
	[VEHICLE_PURCHASE_MODAL_TYPES.INCREASE_OFFER]: BID_TYPES.OFFER,
	[VEHICLE_PURCHASE_MODAL_TYPES.MAKE_OFFER]: BID_TYPES.OFFER,
	[VEHICLE_PURCHASE_MODAL_TYPES.BUY_IT_NOW]: BID_TYPES.OFFER,
});

const vehiclePurchaseModalStateDefaults = {
	modalType: VEHICLE_PURCHASE_MODAL_TYPES.IDLE,
	showModal: false,
	value: 0,
};

const VehicleInfoContext = React.createContext({
	hideVehiclePurchaseModal: noop,
	showVehiclePurchaseModal: noop,
	vehiclePurchaseModalState: vehiclePurchaseModalStateDefaults,
});

VehicleInfoContext.displayName = 'VehicleInfoContext';

const VehicleInfoContextProvider = ({
	children,
	value = {
		vehiclePurchaseModalState: {},
	},
}) => {
	const [vehiclePurchaseModalState, setVehiclePurchaseModalState] = useFullState({
		...vehiclePurchaseModalStateDefaults,
		...value.vehiclePurchaseModalState,
	});

	const showVehiclePurchaseModal = useCallback(
		(state) => {
			setVehiclePurchaseModalState({
				value: 0,
				...state,
				showModal: true,
			});
		},
		[setVehiclePurchaseModalState],
	);

	const hideVehiclePurchaseModal = useCallback(
		(state) => {
			setVehiclePurchaseModalState({
				value: 0,
				...state,
				modalType: VEHICLE_PURCHASE_MODAL_TYPES.IDLE,
				showModal: false,
			});
		},
		[setVehiclePurchaseModalState],
	);

	const contextValue = {
		hideVehiclePurchaseModal,
		showVehiclePurchaseModal,
		vehiclePurchaseModalState,
	};

	return <VehicleInfoContext.Provider value={contextValue}>{children}</VehicleInfoContext.Provider>;
};

VehicleInfoContextProvider.propTypes = {
	children: PropTypes.node.isRequired,
	value: PropTypes.shape({
		vehiclePurchaseModalState: PropTypes.shape({
			modalType: PropTypes.oneOf(Object.keys(VEHICLE_PURCHASE_MODAL_TYPES)),
			showModal: PropTypes.bool,
			value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
		}),
	}),
};

export {
	VEHICLE_PURCHASE_MODAL_BID_TYPES,
	VEHICLE_PURCHASE_MODAL_TYPES,
	VehicleInfoContextProvider,
	VehicleInfoContext,
};
